import { useQuery } from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import { useDriverStateV2 } from './useDriverStateV2';
import { getStoredSelectedCompanyId } from './userCompanies.shared';
import env from '../config/env';
import {
  DriverLeaderboardEntryDto,
  DriverLeaderboardResponseDto,
} from '../types';

/** pg / JSON may return numeric columns as strings; coerce for UI math and comparisons. */
function toFiniteNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function normalizeDriverLeaderboard(
  raw: unknown
): DriverLeaderboardResponseDto {
  const d = raw as Record<string, unknown>;
  const list = Array.isArray(d.leaderboard) ? d.leaderboard : [];
  const leaderboard: DriverLeaderboardEntryDto[] = list.map((entry) => {
    const e = entry as Record<string, unknown>;
    return {
      driverId: String(e.driverId ?? ''),
      driverName: String(e.driverName ?? ''),
      rank: toFiniteNumber(e.rank),
      totalCompletedCalls: toFiniteNumber(e.totalCompletedCalls),
    };
  });
  return { leaderboard };
}

const fetchDriverLeaderboard = async (
  token: string,
  companyId: string,
  startDate?: string,
  endDate?: string
): Promise<DriverLeaderboardResponseDto> => {
  try {
    const params = new URLSearchParams();
    if (startDate) {
      params.append('startDate', startDate);
    }
    if (endDate) {
      params.append('endDate', endDate);
    }

    const queryString = params.toString();
    const url = `${env.apiUrl}/drivers/leaderboard${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-company-id': companyId,
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch driver leaderboard: ${response.statusText}`
      );
    }

    const data = await response.json();
    return normalizeDriverLeaderboard(data);
  } catch (error: any) {
    console.error('Failed to fetch driver leaderboard:', error);
    throw new Error(
      error.response?.data?.message || 'Failed to fetch driver leaderboard'
    );
  }
};

export interface UseDriverLeaderboardOptions {
  startDate?: string;
  endDate?: string;
}

/**
 * Hook to fetch driver leaderboard statistics
 *
 * @param options - Optional filtering parameters
 * @param options.startDate - Start date in ISO format (YYYY-MM-DD)
 * @param options.endDate - End date in ISO format (YYYY-MM-DD)
 */
export const useDriverLeaderboard = (options?: UseDriverLeaderboardOptions) => {
  const { getAccessTokenSilently } = useHybridAuth();
  const { driver } = useDriverStateV2();
  // An office member with no Driver row has no driver company; the stored
  // selection stands in, as in useCompanyStats. Without it the analytics
  // leaderboard never loaded for an owner who does not drive.
  const companyId = driver?.companyId || getStoredSelectedCompanyId();

  return useQuery({
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      if (!companyId) {
        throw new Error('Company ID not available');
      }
      return fetchDriverLeaderboard(
        token,
        companyId,
        options?.startDate,
        options?.endDate
      );
    },
    queryKey: [
      'driver-leaderboard',
      companyId,
      options?.startDate,
      options?.endDate,
    ],
    enabled: !!companyId,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
};
